/**
 * DailyFeedWidget - Extracted from HubDashboard.jsx
 * Shows daily activity feed with posts, alerts, and updates
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Activity, Bell, Truck, LogOut, Shield, Check, Zap, Plus,
  RefreshCw, Star, ThumbsUp, MessageCircle
} from 'lucide-react';
import { Button } from '../../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DailyFeedWidget({ isAdmin }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({ content: '', post_type: 'update', priority: 'normal' });
  const [filter, setFilter] = useState('all');

  const postTypes = [
    { value: 'update', label: 'Update', icon: Activity, color: '#3B82F6' },
    { value: 'alert', label: 'Alert', icon: Bell, color: '#EF4444' },
    { value: 'arrival', label: 'Arrival', icon: Truck, color: '#22C55E' },
    { value: 'departure', label: 'Departure', icon: LogOut, color: '#F97316' },
    { value: 'issue', label: 'Issue', icon: Shield, color: '#EF4444' },
    { value: 'resolution', label: 'Resolved', icon: Check, color: '#22C55E' },
    { value: 'app_event', label: 'App Event', icon: Zap, color: '#8B5CF6' },
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: '#64748B' },
    { value: 'normal', label: 'Normal', color: '#3B82F6' },
    { value: 'high', label: 'High', color: '#F97316' },
    { value: 'urgent', label: 'Urgent', color: '#EF4444' },
  ];

  useEffect(() => {
    fetchPosts();
    const interval = setInterval(fetchPosts, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchPosts = async () => {
    try {
      let url = `${API}/feed?limit=30`;
      if (filter !== 'all') url += `&post_type=${filter}`;
      const res = await axios.get(url, { withCredentials: true });
      setPosts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch feed:', err);
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.content.trim()) return;
    try {
      await axios.post(`${API}/feed`, newPost, { withCredentials: true });
      toast.success('Post created!');
      setShowCreatePost(false);
      setNewPost({ content: '', post_type: 'update', priority: 'normal' });
      fetchPosts();
    } catch (err) {
      toast.error('Failed to create post');
    }
  };

  const togglePin = async (postId, isPinned) => {
    try {
      await axios.put(`${API}/feed/${postId}`, { is_pinned: !isPinned }, { withCredentials: true });
      fetchPosts();
    } catch (err) {
      toast.error('Failed to update post');
    }
  };

  const hidePost = async (postId) => {
    try {
      await axios.put(`${API}/feed/${postId}`, { is_hidden: true }, { withCredentials: true });
      fetchPosts();
      toast.success('Post hidden');
    } catch (err) {
      toast.error('Failed to hide post');
    }
  };

  const reactToPost = async (postId) => {
    try {
      await axios.post(`${API}/feed/${postId}/react?reaction=👍`, {}, { withCredentials: true });
      fetchPosts();
    } catch (err) {
      console.error('Failed to react');
    }
  };

  const getPostTypeInfo = (type) => postTypes.find(t => t.value === type) || postTypes[0];
  const getPriorityInfo = (priority) => priorities.find(p => p.value === priority) || priorities[1];

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000 / 60;
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${Math.floor(diff)}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl border border-border/30">
      {/* Header */}
      <div className="p-4 border-b border-border/30">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Activity className="w-5 h-5 text-accent" />
            Daily Feed
          </h2>
          {isAdmin && (
            <Button size="sm" onClick={() => setShowCreatePost(true)} className="gap-1">
              <Plus className="w-4 h-4" /> Post
            </Button>
          )}
        </div>
        
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${filter === 'all' ? 'bg-accent text-black' : 'bg-secondary'}`}
          >
            All
          </button>
          {postTypes.slice(0, 5).map(type => (
            <button
              key={type.value}
              onClick={() => setFilter(type.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex items-center gap-1 ${filter === type.value ? 'text-black' : ''}`}
              style={{ backgroundColor: filter === type.value ? type.color : 'var(--secondary)' }}
            >
              <type.icon className="w-3 h-3" />
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Posts List */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-accent" />
          </div>
        ) : posts.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Activity className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No updates yet</p>
            {isAdmin && <p className="text-xs mt-1">Click "Post" to add the first update</p>}
          </div>
        ) : (
          <div className="divide-y divide-border/20">
            {posts.map(post => {
              const typeInfo = getPostTypeInfo(post.post_type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <div key={post.post_id} className={`p-4 hover:bg-secondary/20 transition-colors ${post.is_pinned ? 'bg-accent/5 border-l-2 border-l-accent' : ''}`}>
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: typeInfo.color + '20' }}>
                      {post.source_app ? (
                        <span className="text-lg">{post.source_app === 'ridero' ? '🚚' : post.source_app === 'mechanic_kiosk' ? '🔧' : '📱'}</span>
                      ) : (
                        <TypeIcon className="w-5 h-5" style={{ color: typeInfo.color }} />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{post.author_name}</span>
                        {post.source_app && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">{post.source_app}</span>
                        )}
                        {post.priority === 'urgent' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 animate-pulse">URGENT</span>
                        )}
                        {post.priority === 'high' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">HIGH</span>
                        )}
                        {post.is_pinned && (
                          <Star className="w-3 h-3 text-accent fill-accent" />
                        )}
                        <span className="text-xs text-muted-foreground ml-auto">{formatTime(post.created_at)}</span>
                      </div>
                      
                      <p className="text-sm">{post.content}</p>
                      
                      {post.related_entity && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {post.entity_type}: {post.related_entity}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-3 mt-2">
                        <button onClick={() => reactToPost(post.post_id)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-accent">
                          <ThumbsUp className="w-3 h-3" />
                          {post.reactions?.length || 0}
                        </button>
                        <button className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MessageCircle className="w-3 h-3" />
                          {post.comments?.length || 0}
                        </button>
                        {isAdmin && (
                          <>
                            <button onClick={() => togglePin(post.post_id, post.is_pinned)} className="text-xs text-muted-foreground hover:text-accent ml-auto">
                              {post.is_pinned ? 'Unpin' : 'Pin'}
                            </button>
                            <button onClick={() => hidePost(post.post_id)} className="text-xs text-red-400 hover:text-red-300">
                              Hide
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowCreatePost(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Create Post</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Message</label>
                <textarea
                  value={newPost.content}
                  onChange={e => setNewPost({...newPost, content: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  rows={3}
                  placeholder="What's happening?"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Type</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {postTypes.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setNewPost({...newPost, post_type: type.value})}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 ${newPost.post_type === type.value ? 'text-white' : ''}`}
                      style={{ backgroundColor: newPost.post_type === type.value ? type.color : 'var(--secondary)' }}
                    >
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Priority</label>
                <div className="flex gap-2 mt-2">
                  {priorities.map(p => (
                    <button
                      key={p.value}
                      onClick={() => setNewPost({...newPost, priority: p.value})}
                      className={`px-3 py-2 rounded-lg text-sm ${newPost.priority === p.value ? 'text-white' : ''}`}
                      style={{ backgroundColor: newPost.priority === p.value ? p.color : 'var(--secondary)' }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowCreatePost(false)} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={createPost} disabled={!newPost.content.trim()} className="flex-1 py-3 bg-accent text-black rounded-xl font-bold disabled:opacity-50">Post</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
